import { useState, useEffect, useRef, useCallback } from 'react';
import { Socket, Channel } from 'phoenix';

interface ReportProgress {
  job_uuid: string;
  progress_percentage: number;
  progress_message: string;
  status: string;
}

interface ReportResult {
  job_uuid: string;
  status: 'completed' | 'failed' | 'canceled';
  gcs_url?: string;
  file_size_bytes?: number;
  error_message?: string;
}

interface UseReportGeneratorOptions {
  userId: string;
  jwtToken: string;
  websocketUrl: string;
}

interface ReportState {
  isGenerating: boolean;
  progress: number;
  message: string;
  error: string | null;
  downloadUrl: string | null;
}

export function useReportGenerator(options: UseReportGeneratorOptions) {
  const { userId, jwtToken, websocketUrl } = options;

  const [state, setState] = useState<ReportState>({
    isGenerating: false,
    progress: 0,
    message: '',
    error: null,
    downloadUrl: null,
  });

  const socketRef = useRef<Socket | null>(null);
  const channelRef = useRef<Channel | null>(null);
  const currentJobRef = useRef<string | null>(null);

  // Initialize socket and channel
  useEffect(() => {
    if (!userId || !jwtToken) return;

    // Create socket with JWT token
    const socket = new Socket(websocketUrl, {
      params: { token: jwtToken },
    });

    socket.connect();
    socketRef.current = socket;

    // Join the user's reports channel
    const channel = socket.channel(`reports:${userId}`, {});

    channel.join()
      .receive('ok', () => {
        console.log('Connected to reports channel');
      })
      .receive('error', (err: any) => {
        console.error('Failed to join reports channel:', err);
        setState(prev => ({
          ...prev,
          error: 'Failed to connect to report service',
        }));
      });

    channelRef.current = channel;

    // Listen for progress updates
    channel.on('report_progress', (payload: ReportProgress) => {
      if (payload.job_uuid === currentJobRef.current) {
        setState(prev => ({
          ...prev,
          progress: payload.progress_percentage,
          message: payload.progress_message,
        }));
      }
    });

    // Listen for completion
    channel.on('report_completed', (payload: ReportResult) => {
      if (payload.job_uuid === currentJobRef.current) {
        setState(prev => ({
          ...prev,
          isGenerating: false,
          progress: 100,
          message: 'Report ready!',
          downloadUrl: payload.gcs_url || null,
        }));

        // Auto-download
        if (payload.gcs_url) {
          autoDownload(payload.gcs_url);
        }

        currentJobRef.current = null;
      }
    });

    // Listen for failures
    channel.on('report_failed', (payload: ReportResult) => {
      if (payload.job_uuid === currentJobRef.current) {
        setState(prev => ({
          ...prev,
          isGenerating: false,
          error: payload.error_message || 'Report generation failed',
        }));
        currentJobRef.current = null;
      }
    });

    // Listen for cancellation
    channel.on('report_canceled', (payload: ReportResult) => {
      if (payload.job_uuid === currentJobRef.current) {
        setState(prev => ({
          ...prev,
          isGenerating: false,
          progress: 0,
          message: 'Report canceled',
        }));
        currentJobRef.current = null;
      }
    });

    // Cleanup
    return () => {
      channel.leave();
      socket.disconnect();
    };
  }, [userId, jwtToken, websocketUrl]);

  // Generate report function
  const generateReport = useCallback((payload: {
    template_uuid: string;
    facility_id: number;
    runtime_params: Record<string, any>;
  }) => {
    const channel = channelRef.current;
    if (!channel) {
      setState(prev => ({ ...prev, error: 'Not connected' }));
      return;
    }

    setState({
      isGenerating: true,
      progress: 0,
      message: 'Starting report generation...',
      error: null,
      downloadUrl: null,
    });

    channel.push('create_report', payload)
      .receive('ok', (response: { job_uuid: string }) => {
        currentJobRef.current = response.job_uuid;
        console.log('Report job created:', response.job_uuid);
      })
      .receive('error', (err: any) => {
        console.error('Failed to create report:', err);
        setState(prev => ({
          ...prev,
          isGenerating: false,
          error: err.reason || 'Failed to create report',
        }));
      });
  }, []);

  // Cancel report function
  const cancelReport = useCallback(() => {
    const channel = channelRef.current;
    const jobUuid = currentJobRef.current;

    if (!channel || !jobUuid) return;

    // Optimistically cancel on the frontend
    setState(prev => ({
      ...prev,
      isGenerating: false,
      progress: 0,
      message: 'Report canceled',
      error: null,
    }));
    currentJobRef.current = null;

    // Attempt to cancel on backend
    channel.push('cancel_report', { job_uuid: jobUuid })
      .receive('ok', () => {
        console.log('Report canceled successfully');
      })
      .receive('error', (err: any) => {
        // If report is already in terminal state (completed/failed/canceled),
        // we've already updated the UI, so just log it
        console.log('Cancel not needed - report already in terminal state:', err);
      });
  }, []);

  // Auto-download helper
  const autoDownload = (url: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = `report_${Date.now()}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return {
    ...state,
    generateReport,
    cancelReport,
  };
}
